import React from 'react';
import { Box, Button, Chip, Container, Divider, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PREMIUM_FEATURES } from '../constants/premiumFeatures';
import { getPaidFeatureStatus } from '../utils/premiumAccess';

const Membership: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">Account</Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Membership & Access</Typography>
          <Typography color="text.secondary">Review access to each A3Tech feature and its individual expiry date.</Typography>
        </Stack>

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          {PREMIUM_FEATURES.map((feature, index) => {
            const access = getPaidFeatureStatus(user, feature.key);
            return (
              <React.Fragment key={feature.key}>
                {index > 0 && <Divider />}
                <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{feature.label}</Typography>
                      <Chip
                        size="small"
                        label={access.status}
                        sx={(theme) => {
                          const isLight = theme.palette.mode === 'light';
                          const colors = access.active
                            ? isLight ? ['#DCFCE7', '#166534'] : ['#14532D', '#BBF7D0']
                            : access.expired
                              ? isLight ? ['#FEE2E2', '#991B1B'] : ['#7F1D1D', '#FECACA']
                              : isLight ? ['#E2E8F0', '#334155'] : ['#334155', '#E2E8F0'];
                          return {
                            bgcolor: colors[0],
                            color: colors[1],
                            border: '1px solid currentColor',
                            fontWeight: 700,
                            '& .MuiChip-label': { color: 'inherit' },
                          };
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{feature.description}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{access.expiryLabel}</Typography>
                  </Box>
                  <Button
                    variant={access.active ? 'outlined' : 'contained'}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => access.active
                      ? navigate(feature.path)
                      : navigate('/payment', { state: { featureKey: feature.key, featureLabel: feature.label, selectedFeatures: [feature.key] } })}
                    sx={{ minHeight: 44, flexShrink: 0 }}
                  >
                    {access.active ? 'Open' : access.expired ? 'Renew' : 'Get access'}
                  </Button>
                </Box>
              </React.Fragment>
            );
          })}
        </Paper>
      </Container>
    </Box>
  );
};

export default Membership;